import decode from 'jwt-decode';
import {
  DecodedToken,
  REGISTER_SUCCESS,
  LOGIN_SUCCESS,
  LOGOUT_SUCCESS,
  RESUME_SESSION_SUCCESS,
} from './types';
import { Dispatch } from '../types';
import API from '../../api';
import * as authApi from '../../api/auth';
import { User } from '../../types/auth';
import Tracking from '../../utils/tracking';
import * as LocalStorage from '../../utils/localStorage';
import Logger from '../../utils/logger';

export const LOCAL_STORAGE_KEY = 'accessToken';
const HUBSPOT_ENABLED = process.env.NEXT_PUBLIC_HUBSPOT_ENABLED === 'TRUE';

export const login =
  (dispatch: Dispatch) =>
  async (email: string, password: string): Promise<User> => {
    try {
      const { accessToken, referralCode } = await authApi.login(email, password);
      LocalStorage.saveItem(LOCAL_STORAGE_KEY, accessToken);
      API.setAccessToken(accessToken);
      const user = await authApi.getUserInfo();
      const hubspotToken = HUBSPOT_ENABLED ? await getHubspotToken() : null;
      dispatch({ type: LOGIN_SUCCESS, accessToken, referralCode, user, hubspotToken });
      return user;
    } catch (err: any) {
      return Promise.reject(err);
    }
  };

export const logout = (dispatch: Dispatch) => async (): Promise<void> => {
  try {
    await authApi.logout();
  } catch (err: any) {
    Logger.error(err);
  } finally {
    LocalStorage.removeItem(LOCAL_STORAGE_KEY);
    pruneHubspotToken();
    API.unsetAccessToken();
    dispatch({ type: LOGOUT_SUCCESS });
  }
};

export const register =
  (dispatch: Dispatch) =>
  async (email: string, password: string, referrerCode: string): Promise<User> => {
    const { accessToken } = await authApi.register(email, password, referrerCode);
    LocalStorage.saveItem(LOCAL_STORAGE_KEY, accessToken);
    API.setAccessToken(accessToken);
    const user = await authApi.getUserInfo();
    const hubspotToken = HUBSPOT_ENABLED ? await getHubspotToken() : null;
    dispatch({ type: REGISTER_SUCCESS, accessToken, user, hubspotToken });
    return user;
  };

export const resumeSession = (dispatch: Dispatch) => async (): Promise<void> => {
  const accessToken = LocalStorage.getItem(LOCAL_STORAGE_KEY);
  const currentTime = +new Date() / 1000;

  if (!accessToken) return;

  try {
    const { user, exp }: DecodedToken = decode(accessToken);

    if (exp && currentTime >= exp) {
      throw new Error('[Auth] Token expired.');
    }

    API.setAccessToken(accessToken);
    const hubspotToken = HUBSPOT_ENABLED ? await getHubspotToken() : null;
    dispatch({ type: RESUME_SESSION_SUCCESS, user, accessToken, hubspotToken });
    Tracking.identify(user);
  } catch (err: any) {
    // remove invaild token
    LocalStorage.removeItem(LOCAL_STORAGE_KEY);
    pruneHubspotToken();
    Tracking.reset();
  }
};

/**
 * Get a hubspot token in-memory. If not already there attempts to fetch one from API.
 * This token is particularly short-lived (20mins) and is only used once when initating
 * the widget, hence no necessary to store in longer-term storage nor "renewed".
 */
const getHubspotToken = async (): Promise<string> => {
  if (!_hubspotToken) {
    _hubspotToken = await authApi.getHubSpotToken();
  }
  return _hubspotToken;
};

const pruneHubspotToken = (): void => {
  _hubspotToken = null;
};

let _hubspotToken: string | null = null;
